'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { oneLine } from 'common-tags';

import type { FunctionComponent, ReactNode } from 'react';

type CardSliderProps = {
  /** Cada `<li>` (o elemento) va como un slide. */
  children: ReactNode[];
  /**
   * Clases del contenedor scrollable (la `<ul>`). Debe montar el layout
   * responsive: slider en mobile, grid en sm+.
   */
  className?: string;
};

/**
 * Slider horizontal con scroll-snap + bullets, para el bloque de tarjetas
 * destacadas en mobile. El contenedor sigue siendo CSS puro (responsive: slider
 * abajo, grid en sm+); esta capa cliente sólo añade los indicadores, que
 * necesitan JS para saber cuál es la tarjeta activa.
 *
 * Los bullets se ocultan en sm+ (`sm:hidden`), donde ya no hay slider.
 */
export const CardSlider: FunctionComponent<CardSliderProps> = ({
  children,
  className = '',
}) => {
  const ref = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const count = children.length;

  // Tarjeta activa = la que tiene el centro más cerca del centro del viewport
  // del slider. Robusto ante anchos variables y scroll-snap `center`.
  const recompute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(el.children).forEach((child, i) => {
      const c = child as HTMLElement;
      const childCenter = c.offsetLeft + c.offsetWidth / 2;
      const dist = Math.abs(childCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    setActive(best);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    recompute();
    el.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    return () => {
      el.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
    };
  }, [recompute]);

  const goTo = (i: number) => {
    const el = ref.current;
    if (!el) return;
    const child = el.children[i] as HTMLElement | undefined;
    if (!child) return;
    // Centra la tarjeta en el viewport del slider.
    el.scrollTo({
      left: child.offsetLeft - (el.clientWidth - child.offsetWidth) / 2,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <ul ref={ref} className={className}>
        {children}
      </ul>

      {count > 1 && (
        <div
          className="mt-4 flex justify-center gap-2 sm:hidden"
          role="tablist"
          aria-label="Indicadores del carrusel"
        >
          {children.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`Ir a la tarjeta ${i + 1}`}
              onClick={() => goTo(i)}
              className={oneLine`h-2 rounded-full transition-all ${
                i === active
                  ? 'bg-primary-500 w-5'
                  : 'bg-neutrals-200 hover:bg-neutrals-300 w-2'
              }`}
            />
          ))}
        </div>
      )}
    </>
  );
};

CardSlider.displayName = 'CardSlider';

export default CardSlider;
